import { useEffect, useMemo, useState } from "react";
import type { FeedComment } from "@gooner/shared";
import { FEED_COMMENT_MAX } from "@gooner/shared";
import { track } from "../../../analytics";
import { api, userApi } from "../../../lib/api";
import { useSession } from "../../../lib/session";
import { IconTrash } from "../../ui/icons";
import { MessageComposer } from "../../ui/MessageComposer";
import { MessageRow } from "../../ui/MessageRow";
import { AuthModal } from "../AuthModal";
import { MASK } from "../../../lib/replayMask";

/**
 * A post's thread, one level deep: top-level comments, each with its replies indented
 * once. The server returns the thread flat (parentId per row) and this groups it; a
 * reply's form opens inline under the comment it answers. Deleting your own removes it
 * here and — for a top-level comment — its replies with it, the server's rule.
 */
export function CommentThread({ postId, onCount }: { postId: string; onCount: (n: number) => void }) {
  const user = useSession((s) => s.user);
  const [comments, setComments] = useState<FeedComment[] | null>(null);
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [authOpen, setAuthOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .feedComments(postId)
      .then((r) => !cancelled && setComments(r.comments))
      .catch(() => !cancelled && setComments([]));
    return () => {
      cancelled = true;
    };
  }, [postId]);

  const grouped = useMemo(() => {
    const tops = (comments ?? []).filter((c) => c.parentId === null);
    const replies = new Map<string, FeedComment[]>();
    for (const c of comments ?? []) {
      if (c.parentId === null) continue;
      const list = replies.get(c.parentId) ?? [];
      list.push(c);
      replies.set(c.parentId, list);
    }
    return { tops, replies };
  }, [comments]);

  const landed = (c: FeedComment) => {
    setComments((cur) => {
      const next = [...(cur ?? []), c];
      onCount(next.length);
      return next;
    });
    setReplyTo(null);
  };
  const remove = async (c: FeedComment) => {
    try {
      await userApi.feedCommentDelete(c.id);
      track("feed_comment_deleted", { by: "author" });
      setComments((cur) => {
        const next = (cur ?? []).filter((x) => x.id !== c.id && x.parentId !== c.id);
        onCount(next.length);
        return next;
      });
    } catch {
      /* the next open tells the truth */
    }
  };

  // The row is the shared MessageRow (a room's chat draws the same one); what stays
  // here is the thread's own: Reply, Delete, and the inline reply form.
  const row = (c: FeedComment, reply: boolean) => (
    <MessageRow
      key={c.id}
      author={c.author}
      at={c.createdAt}
      indent={reply}
      actions={
        <>
          {!reply ? (
            <button
              type="button"
              onClick={() => (user ? setReplyTo(replyTo === c.id ? null : c.id) : setAuthOpen(true))}
              className="text-zinc-500 hover:text-zinc-200"
            >
              Reply
            </button>
          ) : null}
          {c.mine ? (
            <button
              type="button"
              onClick={() => void remove(c)}
              title="Delete"
              aria-label="Delete this comment"
              className="text-zinc-600 hover:text-zinc-200"
            >
              <IconTrash width={12} height={12} />
            </button>
          ) : null}
        </>
      }
    >
      <p className="whitespace-pre-wrap text-sm text-zinc-200" {...MASK}>{c.body}</p>
      {replyTo === c.id ? (
        <div className="mt-2">
          <CommentForm postId={postId} parentId={c.id} onLanded={landed} onCancel={() => setReplyTo(null)} autoFocus />
        </div>
      ) : null}
    </MessageRow>
  );

  return (
    <div className="space-y-3 border-t border-[var(--color-edge)] px-3 py-3">
      {comments === null ? (
        <p className="text-xs text-zinc-500">Loading…</p>
      ) : grouped.tops.length === 0 ? (
        <p className="text-xs text-zinc-500">Nothing yet — say the first thing.</p>
      ) : (
        grouped.tops.map((c) => (
          <div key={c.id} className="space-y-2">
            {row(c, false)}
            {(grouped.replies.get(c.id) ?? []).map((r) => row(r, true))}
          </div>
        ))
      )}
      {user ? (
        <CommentForm postId={postId} parentId={null} onLanded={landed} />
      ) : (
        <button type="button" onClick={() => setAuthOpen(true)} className="text-xs text-zinc-400 hover:text-zinc-100">
          Sign in to comment
        </button>
      )}
      {authOpen ? <AuthModal initialMode="register" reason="Create a free account to join the thread." onClose={() => setAuthOpen(false)} /> : null}
    </div>
  );
}

function CommentForm({
  postId,
  parentId,
  onLanded,
  onCancel,
  autoFocus,
}: {
  postId: string;
  parentId: string | null;
  onLanded: (c: FeedComment) => void;
  onCancel?: () => void;
  autoFocus?: boolean;
}) {
  return (
    <MessageComposer
      maxLength={FEED_COMMENT_MAX}
      placeholder={parentId ? "Reply…" : "Say something…"}
      submitLabel={parentId ? "Reply" : "Post"}
      onCancel={onCancel}
      autoFocus={autoFocus}
      onSend={async (text) => {
        const r = await userApi.feedComment(postId, text, parentId ?? undefined);
        track("feed_comment_posted", { reply: parentId !== null });
        onLanded(r.comment);
      }}
    />
  );
}
